/** Build / normalize influencer commission promo share URLs (attribution links). */

import { ENV, SITE_URL } from '../configurations/api/url';

function looksLikeObjectId(value: string | null | undefined): boolean {
    return !!value && /^[a-f0-9]{24}$/i.test(String(value).trim());
}

/** Public frontend referral page for Circuul app-install / web-visit codes. */
export function buildFrontendReferralUrl(code: string): string | null {
    const normalized = String(code || '')
        .trim()
        .split(/[?&#/\s]/)[0]
        .toUpperCase();
    if (!normalized) return null;

    if (typeof window !== 'undefined') {
        return `${window.location.origin}/r/${encodeURIComponent(normalized)}`;
    }

    const web = String(ENV.URL ?? SITE_URL ?? '').replace(/\/+$/, '');
    return web ? `${web}/r/${encodeURIComponent(normalized)}` : null;
}

/** Rewrite legacy API Circuul share URLs to the frontend /r/:code page. */
export function normalizeApiCircuulShareUrl(shareUrl: string | null | undefined): string | null {
    if (!shareUrl?.trim()) return null;
    const match = shareUrl.trim().match(/\/circuul\/r\/([^/?#]+)/i);
    if (!match?.[1]) return null;
    try {
        return buildFrontendReferralUrl(decodeURIComponent(match[1]));
    } catch {
        return buildFrontendReferralUrl(match[1]);
    }
}

/**
 * Contest public pages live at /campaign/{slug}?ref=CODE
 * (legacy /contest/... share URLs are rewritten to /campaign/...).
 * Paths that still use a raw ObjectId are treated as invalid so callers can rebuild with a slug.
 */
export function localizeCommissionShareUrl(shareUrl: string | null | undefined): string | null {
    if (!shareUrl?.trim()) return null;
    const raw = shareUrl.trim();
    try {
        const absolute = raw.startsWith('/')
            ? `${typeof window !== 'undefined' ? window.location.origin : 'https://thspian.com'}${raw}`
            : raw;
        const u = new URL(absolute);
        u.pathname = rewriteContestPathname(u.pathname);
        const segment = publicCampaignPathSlug(u.pathname);
        if (looksLikeObjectId(segment)) {
            // ObjectId links are not valid public share URLs — force rebuild with slug.
            return null;
        }
        if (typeof window === 'undefined') {
            return `${u.pathname}${u.search}${u.hash}`;
        }
        return `${window.location.origin}${u.pathname}${u.search}${u.hash}`;
    } catch {
        return null;
    }
}

function rewriteContestPathname(pathname: string): string {
    const m = pathname.match(/^\/contest\/([^/]+)\/?$/i);
    if (m?.[1]) return `/campaign/${m[1]}`;
    return pathname;
}

function publicCampaignPathSlug(pathname: string): string | null {
    const m = pathname.match(/^\/campaign\/([^/]+)\/?$/i);
    return m?.[1] ? decodeURIComponent(m[1]) : null;
}

export function buildCommissionShareUrl(opts: {
    code: string;
    kind?: string | null;
    /** Contest public slug (preferred) or id */
    contestSlug?: string | null;
    contestId?: string | null;
    campaignId?: string | null;
    shareUrlFromApi?: string | null;
}): string | null {
    const code = String(opts.code || '')
        .trim()
        .toUpperCase();
    if (!code) return null;

    // Circuul (app_install / web_visit) — always share the frontend /r/:code page (env-aware).
    if (opts.kind === 'app_install' || opts.kind === 'web_visit') {
        return normalizeApiCircuulShareUrl(opts.shareUrlFromApi) || buildFrontendReferralUrl(code);
    }

    const fromApi = localizeCommissionShareUrl(opts.shareUrlFromApi);
    if (fromApi) return fromApi;

    if (typeof window === 'undefined') return null;
    const origin = window.location.origin;

    const slug = String(opts.contestSlug || '').trim();
    const contestKey = slug && !looksLikeObjectId(slug) ? slug : '';
    if (contestKey) {
        return `${origin}/campaign/${encodeURIComponent(contestKey)}?ref=${encodeURIComponent(code)}`;
    }

    // Contest commission links must use the linked contest slug — never campaign/_id.
    if (opts.kind === 'contest' || opts.contestId || opts.contestSlug) {
        return null;
    }

    if (opts.campaignId) {
        return `${origin}/campaigns/${opts.campaignId}?ref=${encodeURIComponent(code)}`;
    }
    return `${origin}/r/${encodeURIComponent(code)}`;
}
